import React from "react";
import { useTranslation } from "react-i18next";
import { FiKey, FiLock, FiShield } from "react-icons/fi";
import { Badge, Card } from "components/ui";
import ChangePassword from "./AccountForgetPassword";
import { AccountEditButton, AccountSettingRow, accountValueInputClass } from "./AccountSettingRow";

interface AccountSecurityProps {
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  isPopupOpen1: boolean;
  setIsPopupOpen1: (value: boolean) => void;
  togglepopup1: () => void;
  mfaEnabled: boolean;
  onShowBackupCodes: () => void;
  handleMFAToggle: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

/** "Security" card: password and multi-factor authentication. */
export const AccountSecurity = ({
  handleKeyDown,
  isPopupOpen1,
  setIsPopupOpen1,
  togglepopup1,
  mfaEnabled,
  onShowBackupCodes,
  handleMFAToggle,
}: AccountSecurityProps) => {
  const { t } = useTranslation();

  return (
    <Card title="Security" subtitle="Keep your account and your rewards protected." flush>
      <div className="divide-y divide-line">
        <AccountSettingRow
          icon={<FiLock />}
          label={<label className="mb-0">{t("Account.Label.change_password")}</label>}
          action={<AccountEditButton onClick={togglepopup1} label={t("Account.Label.change_password")} />}
        >
          <input
            type="password"
            className={`${accountValueInputClass} tracking-widest`}
            placeholder="********"
            onKeyDown={handleKeyDown}
          />
          {isPopupOpen1 && (
            <div>
              <ChangePassword isPopupOpen1={() => setIsPopupOpen1(false)} />
            </div>
          )}
        </AccountSettingRow>

        <AccountSettingRow
          icon={<FiShield />}
          label="Multi-Factor Authentication (MFA)"
          description="Require a code from your authenticator app when you sign in."
          action={
            <button
              type="button"
              role="switch"
              aria-checked={mfaEnabled}
              aria-label="Multi-Factor Authentication"
              onClick={handleMFAToggle}
              className={`relative inline-flex items-center w-11 h-6 rounded-full shrink-0 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-ring ${
                mfaEnabled ? "bg-primary" : "bg-line-strong"
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-surface shadow-sm transition-transform cursor-pointer ${
                  mfaEnabled ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
          }
        >
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <Badge tone={mfaEnabled ? "success" : "neutral"} dot>
              MFA is {mfaEnabled ? "enabled" : "disabled"} on your account
            </Badge>
            {mfaEnabled && (
              <button type="button" onClick={onShowBackupCodes} className="inline-flex items-center gap-1.5 text-sm zs-link">
                <FiKey className="w-3.5 h-3.5" />
                Backup codes
              </button>
            )}
          </div>
        </AccountSettingRow>
      </div>
    </Card>
  );
};
